import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { usePermissions } from "../lib/permissions";
import api from "../lib/api";
import TrialBanner from "./TrialBanner";
import AlertsBanner from "./AlertsBanner";
import { VersionBadge } from "./VersionWatcher";
import {
  ChartBar, Users, Package, Receipt, Truck, UserList, MapPin, Printer,
  SignOut, List, X, Sparkle, CreditCard, Crown, CurrencyEur, Palette, Buildings, HandHeart, ShoppingCart, Factory, Wallet, Database,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALL_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: ChartBar, roles: ["admin", "contabilita", "logistica"] },
  { to: "/clienti", label: "Clienti", icon: Users, section: "clienti", roles: ["admin", "contabilita", "logistica"] },
  { to: "/articoli", label: "Articoli", icon: Package, section: "articoli", roles: ["admin", "contabilita", "logistica"] },
  { to: "/listini", label: "Listini", icon: Receipt, section: "listini", roles: ["admin", "contabilita", "logistica"] },
  { to: "/offerte", label: "Offerte & Preventivi", icon: HandHeart, section: "offerte", roles: ["admin", "contabilita", "logistica"] },
  { to: "/ordinativi", label: "Ordinativi", icon: ShoppingCart, section: "ordinativi", roles: ["admin", "logistica", "contabilita"] },
  { to: "/ddt", label: "D.D.T.", icon: Truck, section: "ddt", roles: ["admin", "logistica", "contabilita"] },
  { to: "/consegne", label: "Consegne", icon: MapPin, section: "ddt", roles: ["admin", "logistica", "contabilita"] },
  { to: "/fatture", label: "Fatturazione", icon: Receipt, section: "fatture", roles: ["admin", "contabilita", "logistica"] },
  { to: "/sospesi", label: "Sospesi", icon: CurrencyEur, section: "sospesi", roles: ["admin", "contabilita", "logistica"] },
  { to: "/fornitori", label: "Fornitori", icon: Factory, section: "fornitori", roles: ["admin", "contabilita", "logistica"] },
  { to: "/fornitori/ddt", label: "DDT ricevuti", icon: Truck, section: "ddt_ricevuti", roles: ["admin", "logistica", "contabilita"] },
  { to: "/fornitori/fatture", label: "Fatture fornitore", icon: Receipt, section: "fatture_fornitore", roles: ["admin", "contabilita", "logistica"] },
  { to: "/costi", label: "Costi", icon: Wallet, section: "costi", roles: ["admin", "contabilita", "logistica"] },
  { to: "/dipendenti", label: "Dipendenti", icon: Users, section: "dipendenti", roles: ["admin", "contabilita", "logistica"] },
  { to: "/automezzi", label: "Automezzi", icon: Truck, roles: ["admin", "contabilita"] },
  { to: "/report", label: "Report & BI", icon: ChartBar, section: "report", roles: ["admin", "contabilita", "logistica"] },
  { to: "/utenti", label: "Utenti & Audit", icon: UserList, roles: ["admin"] },
  { to: "/azienda", label: "Anagrafica azienda", icon: Buildings, roles: ["admin"] },
  { to: "/stampa", label: "Stampa & Totem", icon: Printer, roles: ["admin"] },
  { to: "/backup-dati", label: "Backup & Dati", icon: Database, roles: ["admin"] },
  { to: "/billing", label: "Abbonamento", icon: CreditCard, roles: ["admin"] },
  { to: "/admin", label: "Pannello SaaS", icon: Crown, roles: ["superadmin"] },
  { to: "/admin/superadmins", label: "Superadmin", icon: Crown, roles: ["superadmin"] },
  { to: "/admin/branding", label: "Branding", icon: Palette, roles: ["superadmin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const { canView, isAdmin } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const touchStart = useRef(null);

  // First-run setup wizard: if admin + anagrafica azienda non completa → forza /azienda
  useEffect(() => {
    if (user?.role !== "admin") return;
    if (location.pathname === "/azienda" || location.pathname === "/billing") return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/company");
        if (!cancelled && !data.setup_complete) {
          navigate("/azienda?first=1", { replace: true });
        }
      } catch (e) {
        // silently ignore
      }
    })();
    return () => { cancelled = true; };
  }, [user?.role, location.pathname, navigate]);

  // Swipe-from-left gesture to open sidebar (iPad/mobile only, xl viewports use sticky sidebar)
  useEffect(() => {
    const XL_BP = 1280;
    const EDGE = 24; // px from left edge to start swipe
    const THRESHOLD_OPEN = 60; // min horizontal distance to trigger open
    const THRESHOLD_CLOSE = 60; // min horizontal distance to trigger close
    const MAX_VERTICAL = 80; // abort if user scrolls vertically instead

    const onStart = (e) => {
      if (window.innerWidth >= XL_BP) return;
      const t = e.touches[0];
      // Open gesture: only when closed + started from left edge
      if (!mobileOpen && t.clientX <= EDGE) {
        touchStart.current = { x: t.clientX, y: t.clientY, mode: "open" };
        return;
      }
      // Close gesture: only when open + started inside sidebar area
      if (mobileOpen && t.clientX <= 256) {
        touchStart.current = { x: t.clientX, y: t.clientY, mode: "close" };
      }
    };

    const onEnd = (e) => {
      const s = touchStart.current;
      if (!s) return;
      touchStart.current = null;
      const t = (e.changedTouches && e.changedTouches[0]) || null;
      if (!t) return;
      const dx = t.clientX - s.x;
      const dy = Math.abs(t.clientY - s.y);
      if (dy > MAX_VERTICAL) return;
      if (s.mode === "open" && dx > THRESHOLD_OPEN) setMobileOpen(true);
      if (s.mode === "close" && dx < -THRESHOLD_CLOSE) setMobileOpen(false);
    };

    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchend", onEnd);
    };
  }, [mobileOpen]);

  const items = ALL_ITEMS.filter(i => {
    if (!i.roles) return true;
    if (user.role === "superadmin") return true;
    // Non-superadmin users should NEVER see superadmin-only items, even if admin
    if (i.roles.length === 1 && i.roles[0] === "superadmin") return false;
    const roleOk = i.roles.includes(user.role) || user.role === "admin";
    if (!roleOk) return false;
    // Se la voce è associata a una `section` di permessi, controlla anche canView
    // (admin sempre true via `isAdmin`).
    if (i.section && !isAdmin && !canView(i.section)) return false;
    return true;
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={`fixed xl:sticky top-0 left-0 z-40 w-64 h-screen bg-slate-900 text-white flex flex-col border-r border-slate-800 transform transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full xl:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
          <div
            className="rounded-md bg-gradient-to-br from-azzurro to-indaco flex items-center justify-center overflow-hidden flex-shrink-0"
            style={{ width: "calc(32px * var(--logo-scale, 1))", height: "calc(32px * var(--logo-scale, 1))" }}
          >
            {branding.logo_url ? (
              <img src={`${BACKEND}${branding.logo_url}`} alt={branding.app_name} className="w-full h-full object-contain" />
            ) : (
              <Sparkle size={18} weight="fill" className="text-white" style={{ transform: "scale(var(--logo-scale, 1))" }} />
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold leading-none tracking-tight truncate" style={{ fontSize: "calc(1.125rem * var(--text-scale, 1))" }}>{branding.app_name}</div>
            <div className="text-slate-400 font-mono uppercase tracking-widest mt-0.5 truncate" style={{ fontSize: "calc(10px * var(--text-scale, 1))" }}>{branding.tagline}</div>
          </div>
          <button className="ml-auto xl:hidden text-slate-400 hover:text-white" onClick={() => setMobileOpen(false)} data-testid="close-menu-btn">
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          <div className="overline px-3 mb-2 text-slate-500">Operativo</div>
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end
              data-testid={`nav-${it.to.replace("/", "")}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `sidebar-item ${isActive ? "sidebar-item-active" : "text-slate-300"}`
              }
            >
              <it.icon size={18} weight="regular" />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-celeste to-indaco flex items-center justify-center text-sm font-bold">
              {user.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-widest font-mono">{user.role}</div>
            </div>
            <button onClick={handleLogout} className="text-slate-400 hover:text-white" data-testid="logout-btn" title="Esci">
              <SignOut size={18} />
            </button>
          </div>
          <div className="mt-2 text-center">
            <VersionBadge />
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <TrialBanner />
        <header className="h-16 xl:hidden bg-white border-b border-slate-200 flex items-center px-4 sticky top-0 z-30">
          <button onClick={() => setMobileOpen(true)} className="text-slate-600" data-testid="open-menu-btn">
            <List size={24} />
          </button>
          <div className="ml-4 font-display font-bold text-lg">{branding.app_name}</div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 animate-fade-in">
          <AlertsBanner />
          {children}
        </main>
      </div>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 xl:hidden" onClick={() => setMobileOpen(false)}></div>}

      {/* Swipe hint: subtle edge indicator on iPad/mobile when sidebar is closed */}
      {!mobileOpen && (
        <div
          className="fixed left-0 top-1/2 -translate-y-1/2 w-1 h-16 bg-indaco/30 rounded-r-full xl:hidden pointer-events-none animate-pulse"
          aria-hidden="true"
          data-testid="swipe-hint"
        />
      )}
    </div>
  );
}
